import { screenLocale } from './round-config'
import { confirmNeed, isContact, isSuggestion, standingOf, upcomingVisits, type ConfirmNeed, type NeedVisit } from './planning'
import { acceptedReplyDates, messagesThisCycle, replyConversations, replyForJob, secondMessageFrom, timeIsAgreed } from './rebook-message'
import { paymentLabel } from './payment'
import { dayLabel, shortDate, todayISO } from './ui'
import type { Clean, Customer } from './types'
import { setTimesOn, visibleTime } from './business-settings'

// One set of words for a visit, wherever it shows: the Diary (computer and phone) and Messages (JOINT-PLAN section 1,
// Liam approved 8 Oct 2026). Two short answers, always in this order:
//   the day:  "Not asked yet" · "Asked 8 Oct, no reply" · "Dates offered: Fri 9 · Sat 10, waiting" · "Day agreed"
//   the time: "no set time" · "time to confirm" · "time agreed"
// then, when the customer said something that matters before anyone goes, where they stand ("Ask again 7 Nov",
// "Paused", "Stopped", "Taken off", "Recanvass", "Needs a visit", "Lapsed") and "check before going" when that
// makes the visit one to check (planning.ts confirmNeed). Done, cancelled, Roundly's suggestions and contact
// reminders have one phrase each instead.

export type WordsCustomer = Partial<Pick<Customer, 'status' | 'customer_list' | 'messages_shelf' | 'message_reply' | 'message_reply_on' |
  'message_reply_dates' | 'message_reply_job_id' | 'message_replies' | 'reply_states' | 'reply_agreed' | 'reply_done' | 'reply_answered' | 'follow_up_on' |
  'rebook_messages' | 'rebook_messaged_on' | 'last_clean' | 'contact_note' | 'upcoming_visits' | 'next_confirmed' | 'message_by'>>
export type WordsVisit = NeedVisit & Partial<Pick<Clean, 'job_id' | 'payment_status' | 'payment_method' | 'chased' | 'amount_pence' | 'cash_collected_pence' | 'moved_from' | 'moved_on' | 'moved_told' | 'dated_on' | 'from_credit_pence' | 'paid_in_pence'>>
export interface VisitWords {
  day: string        // the day answer, '' for done / cancelled / suggestion / contact reminder
  time: string       // the time answer, '' likewise
  context: string    // where the customer stands, '' when on the round
  need: ConfirmNeed  // confirmNeed's answer for this visit
  tone: 'ok' | 'note' | 'wait' | 'done'
  text: string       // the whole line, e.g. "Day agreed · time to confirm"
}

const DAY_FMT: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric' }
const DAY_MONTH_FMT: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }
// "Fri 9 · Sat 10"; the month joins in when the days span two months. At most three, then "+2 more".
export function offeredDaysText(dates: readonly string[]): string {
  const days = [...dates].sort()
  if (!days.length) return ''
  const fmt = days[0].slice(0, 7) === days[days.length - 1].slice(0, 7) ? DAY_FMT : DAY_MONTH_FMT
  const shown = days.slice(0, 3).map(d => new Date(`${d}T12:00:00`).toLocaleDateString(fmt === DAY_FMT ? 'en-GB' : screenLocale(), fmt))
  return `${shown.join(' · ')}${days.length > 3 ? ` +${days.length - 3} more` : ''}`
}

// The days this visit's job was offered and is waiting on, oldest first; empty when nothing is waiting. A recorded
// "Send some dates" reply first. Otherwise several days offered in the FIRST message, Liam's usual way (Release 2,
// roundly-bugs): they live only in the contact note's machine-written line, which acceptedReplyDates recovers (main job,
// this cycle's latest message). One day offered is not "dates offered": that day became a visit, which reads "Asked".
export function offeredDays(c: WordsCustomer | null, jobId: number | null = null): string[] {
  if (!c) return []
  const r = replyForJob(c as Parameters<typeof replyForJob>[0], jobId)
  if (r.message_reply === 'dates') {
    // Answered: a day picked from them, even when that visit has moved since (diary #9).
    if (r.reply_agreed || r.reply_done || r.reply_answered) return []
    try {
      const list = JSON.parse(r.message_reply_dates ?? '[]') as unknown
      return Array.isArray(list) ? list.filter((d): d is string => typeof d === 'string').sort() : []
    } catch { return [] }
  }
  if (r.message_reply || jobId !== null || messagesThisCycle(c) === 0) return []
  // Answered once a visit of theirs from that message's day on is agreed, even with no reply recorded (the day agreed
  // straight from the Diary or the booking form): they are not waiting on the days any more (Liam's screenshot, Peggy,
  // 9 Oct 2026: "Offered to … Peggy, waiting" over her agreed visit).
  if (agreedSince(c, c.rebook_messaged_on ?? null)) return []
  const first = acceptedReplyDates({ ...c, message_reply_dates: null, message_reply_job_id: null }, null)
  return first.length > 1 ? first : []
}
// An agreed main-job visit dated on or after `since`: from the customer's upcoming visits when the read carries them,
// else their next agreed visit's date.
function agreedSince(c: WordsCustomer, since: string | null): boolean {
  const visits = upcomingVisits(c)
  if (visits) return visits.some(v => v.confirmed === 1 && (v.job_id ?? null) === null && (!since || v.date >= since))
  return !!c.next_confirmed && (!since || c.next_confirmed >= since)
}

// Every job with days offered and still waiting (one customer can wait on two jobs), for Plan work's offers list.
export function offersWaiting(c: WordsCustomer): { jobId: number | null; dates: string[] }[] {
  return replyConversations(c as Parameters<typeof replyConversations>[0])
    .map(r => ({ jobId: r.message_reply_job_id ?? null, dates: offeredDays(r, r.message_reply_job_id ?? null) }))
    .filter(o => o.dates.length > 0)
}

// The day answer while the day is not agreed: "Dates offered: Fri 9 · Sat 10, waiting", "Asked 6 Oct, no reply" or
// "Not asked yet". Also on a Diary row for a customer who is due and has no visit yet.
export function askWords(c: WordsCustomer | null, jobId: number | null = null): string {
  const offered = offeredDays(c, jobId)
  if (offered.length) return `Dates offered: ${offeredDaysText(offered)}, waiting`
  if (c && messagesThisCycle(c) > 0) return c.rebook_messaged_on ? `Asked ${shortDate(c.rebook_messaged_on)}, no reply` : 'Asked, no reply'
  return 'Not asked yet'
}

// Their last message came before this visit was put on its day (Liam, 11 Oct 2026: Lucy, asked 16 Sep about another
// day, put on Tuesday, read "Asked 16 Sep, no reply" with Mark confirmed): it was not about this day, so they are not
// asked yet and the visit's main button messages them. Their first job only, and not while days they were offered wait.
export function askedBeforeThisDay(v: Pick<WordsVisit, 'job_id' | 'dated_on'>, c: WordsCustomer | null): boolean {
  if (!c || v.job_id || !v.dated_on || !c.rebook_messaged_on || messagesThisCycle(c) === 0 || offeredDays(c, null).length) return false
  return c.rebook_messaged_on < v.dated_on
}
// Asked about another day, and still inside the wait after that message: said so ("Asked 9 Oct about another day, no
// reply"); after the wait, "Not asked yet". Either way the visit's main button messages them, and their pop-up says
// whether a message can go yet (review: the row said "Not asked yet" while no message could go for a week).
function askedAnotherDay(c: WordsCustomer, today: string): string {
  const from = secondMessageFrom(c)
  return from && today < from ? `Asked ${shortDate(c.rebook_messaged_on)} about another day, no reply` : 'Not asked yet'
}

// An agreed visit moved to another day, and the customer not messaged since the move (Release 2; MOVED-FACT-from-diary.md):
// Messages' "Visit moved, tell them" and the Diary's "Moved from Thu 8 Oct, tell them". The server says whether a message
// was recorded after the move (moved_told, from the audit rows, so a message earlier the same day does not count). A
// saved copy from before moved_told falls back to the day: messaged on or after the move's day.
export function movedUntold(v: Pick<WordsVisit, 'confirmed' | 'moved_from' | 'moved_on' | 'moved_told'>, c: WordsCustomer | null): boolean {
  if (v.confirmed || !v.moved_from) return false
  // Moved back to the day they were told: nothing to tell (To tell gap 2).
  if ((v as { date?: string }).date === v.moved_from) return false
  if (v.moved_told !== undefined && v.moved_told !== null) return !v.moved_told
  return !(c?.rebook_messaged_on && v.moved_on && c.rebook_messaged_on >= v.moved_on)
}

const STANDING_WORDS = { visit: 'Needs a visit', paused: 'Paused', recanvass: 'Recanvass', lapsed: 'Lapsed' } as const
// Where the customer stands, in the words above; '' when they are simply on the round.
export function standingWords(c: WordsCustomer | null, today = todayISO()): string {
  if (!c) return ''
  const s = standingOf(c as Parameters<typeof standingOf>[0], today)
  if (s === 'round') return ''
  if (s === 'later') return `Ask again ${shortDate(c.follow_up_on)}`
  if (s === 'taken_off') return !c.customer_list && c.status === 'paused' && c.message_reply === 'stopped' ? 'Stopped' : 'Taken off'
  return STANDING_WORDS[s]
}

// "Done · paid cash" / "Done · not paid": the payment words Owed uses, as the second half of one line.
// The payment half of a done visit's words, as Owed says it: "Not paid", "Chased · not paid", "£15.00 to pay" (part
// paid, where the read carries the money), "Payment not recorded", "Paid cash", "Paid" (bank). The Diary shows it under
// the price, a link to Owed while it is not paid (docs/DIARY-AND-MESSAGES.md, Owed; Liam 8 Oct 2026).
export function paymentWords(v: WordsVisit): string {
  const label = paymentLabel(v).text
  return label === 'Unpaid' ? 'Not paid' : label.replace('· unpaid', '· not paid')
}
function doneWords(v: WordsVisit): string {
  const words = paymentWords(v)
  return `Done · ${words.charAt(0).toLowerCase()}${words.slice(1)}`
}

export function visitWords(v: WordsVisit, c: WordsCustomer | null, today = todayISO()): VisitWords {
  const state = v.state ?? 'booked'
  const whole = (text: string, tone: VisitWords['tone']): VisitWords => ({ day: '', time: '', context: '', need: null, tone, text })
  if (state === 'done') return whole(doneWords(v), v.payment_status === 'paid' ? 'ok' : 'done')
  if (state === 'cancelled') return whole('Cancelled, not counted', 'wait')
  const visit = { ...v, state }
  if (isContact(visit)) return whole('Contact reminder, not a visit', 'wait')
  if (isSuggestion(visit)) return whole('Suggested by Roundly, not booked', 'wait')
  // A visit whose day has passed has nothing to tell them about (Liam, 9 Oct 2026, Past visits to review).
  const day = v.confirmed ? 'Day agreed' : movedUntold(v, c) && v.date >= today ? `Moved from ${dayLabel(v.moved_from!)}, tell them` : askedBeforeThisDay(v, c) ? askedAnotherDay(c!, today) : askWords(c, v.job_id ?? null)
  // A round that gives no set times (Your business) has no time half at all: "Day agreed", not "Day agreed · no set time".
  const time = !setTimesOn() ? '' : !visibleTime(v) ? 'no set time' : timeIsAgreed(visit) ? 'time agreed' : 'time to confirm'
  const context = standingWords(c, today)
  const need = confirmNeed(visit, c, today)
  const tone = need === 'check' || v.confirmed && time === 'time to confirm' ? 'note' : v.confirmed ? 'ok' : 'wait'
  return { day, time, context, need, tone, text: [day, time, context, need === 'check' ? 'check before going' : ''].filter(Boolean).join(' · ') }
}

// The customer facts a Diary visit carries (GET /api/schedule, customer_ prefixed), in the shape above. known: the
// customer from the customers list the Diary also reads, for the contact note (days offered in a first message).
export function diaryCustomer(cl: Clean, known?: Pick<Customer, 'contact_note'> & Partial<Pick<Customer, 'upcoming_visits' | 'next_confirmed'>> | null): WordsCustomer {
  return {
    contact_note: known?.contact_note ?? null,
    // Their agreed visits, when the customers list is known: an agreed day answers days offered (offeredDays).
    ...(known && 'upcoming_visits' in known ? { upcoming_visits: known.upcoming_visits } : {}),
    ...(known && 'next_confirmed' in known ? { next_confirmed: known.next_confirmed } : {}),
    // Their way (0036), carried for the Diary's Don't message visits (tidy-up 2 item 3, option A); these words never read it.
    message_by: cl.customer_message_by ?? null,
    status: cl.customer_status as Customer['status'] | undefined, customer_list: cl.customer_list, messages_shelf: cl.customer_messages_shelf,
    message_reply: cl.customer_message_reply, message_reply_on: cl.customer_message_reply_on, message_reply_dates: cl.customer_message_reply_dates,
    message_reply_job_id: cl.customer_message_reply_job_id, message_replies: cl.customer_message_replies, reply_states: cl.customer_reply_states,
    // An extra job's visit reads that job's own Messages count (several jobs), as Messages does.
    follow_up_on: cl.customer_follow_up_on,
    rebook_messages: cl.job_id != null && cl.job_rebook_messages !== undefined ? cl.job_rebook_messages : cl.customer_rebook_messages,
    rebook_messaged_on: cl.job_id != null && cl.job_rebook_messages !== undefined ? cl.job_rebook_messaged_on : cl.customer_rebook_messaged_on,
    last_clean: cl.customer_last_clean,
  }
}
